import { createContext, useContext } from "react";

export interface MarkdownActionsContextValue {
    onNavigate: (target: string) => void;
    // `handle` is the opaque cite:// citation handle (when the source came from
    // a native citation chip); it lets the handler open the viewer at the
    // resolved page with the bounding box highlighted.
    onSource: (documentId: number, page?: number, handle?: string) => void;
}

const NOOP_ACTIONS: MarkdownActionsContextValue = {
    onNavigate: () => {},
    onSource: () => {},
};

const MarkdownActionsContext =
    createContext<MarkdownActionsContextValue>(NOOP_ACTIONS);

export const MarkdownActionsProvider = MarkdownActionsContext.Provider;
export const useMarkdownActions = () => useContext(MarkdownActionsContext);

// ---------------------------------------------------------------------------
// Custom link protocol parsing
// ---------------------------------------------------------------------------

// The `<a>` override in `markdown-text.tsx` renders custom-protocol links
// (`link://` / `button://`) as `<Button>` elements, which strips the original
// href from the DOM. We mirror it onto this data attribute so DOM-level
// consumers like `parseDomTable` can still recover the citation target when
// exporting a rendered table to xlsx.
export const CUSTOM_HREF_ATTR = "data-custom-href";

type LinkProtocol = "button" | "link" | "cite";
type LinkAction = "navigate" | "modal" | "source";

export interface ParsedLink {
    protocol: LinkProtocol;
    action: LinkAction;
    target: string;
    params?: Record<string, string>;
}

export function parseLink(href: string): ParsedLink | null {
    const protocolMatch = href.match(/^(button|link|cite):\/\//);
    if (!protocolMatch) return null;

    const protocol = protocolMatch[1] as LinkProtocol;
    const withoutProtocol = href.replace(`${protocol}://`, "");

    // `cite://cN` carries an opaque server-stamped handle as its whole body;
    // it always resolves to a source action. A handle-less `cite://` is
    // malformed model output and falls through to null.
    if (protocol === "cite") {
        if (!withoutProtocol) return null;
        return {
            protocol,
            action: "source",
            target: withoutProtocol,
            params: {},
        };
    }

    const [action, ...rest] = withoutProtocol.split("/");
    if (!["navigate", "modal", "source"].includes(action)) return null;

    const targetWithParams = rest.join("/");
    const [target, queryString] = targetWithParams.split("?");

    if (!target) return null;

    const params: Record<string, string> = {};
    if (queryString) {
        new URLSearchParams(queryString).forEach((v, k) => (params[k] = v));
    }

    return { protocol, action: action as LinkAction, target, params };
}
